import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Button, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, LinearProgress, Tooltip, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import FolderIcon from '@material-ui/icons/Folder';
import FolderOpenIcon from '@material-ui/icons/FolderOpen';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import CloseIcon from '@material-ui/icons/Close';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import CreateNewFolderIcon from '@material-ui/icons/CreateNewFolder';
import NoteAddIcon from '@material-ui/icons/NoteAdd';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';

// Files rendered per expanded folder before "Show more", keeps 10k-file folders responsive
const FOLDER_PAGE_SIZE = 100;

// OS metadata files that should never be uploaded
const IGNORED_FILES = ['.DS_Store', 'Thumbs.db', 'desktop.ini'];

const useStyles = makeStyles({
  dropArea: {
    border: '2px dashed #c4c4c4',
    borderRadius: 6,
    padding: '28px 16px',
    textAlign: 'center',
    background: '#fafafa',
    transition: 'border-color 0.15s, background 0.15s',
  },
  // Once files are picked the drop area collapses to one line so the list gets the space
  dropAreaCompact: {
    padding: '8px 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    textAlign: 'left',
  },
  dropAreaActive: {
    borderColor: '#3f51b5',
    background: '#eef0fb',
  },
  uploadIcon: { fontSize: 44, color: '#9e9e9e' },
  uploadIconCompact: { fontSize: 22, color: '#9e9e9e', marginRight: 8, verticalAlign: 'middle' },
  pickButtons: { marginTop: 12, '& button': { margin: '0 6px' } },
  pickButtonsCompact: { flexShrink: 0, '& button': { marginLeft: 8 } },
  summary: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '10px 0 6px' },
  list: { maxHeight: 340, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 4 },
  groupRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 4px 6px 4px',
    cursor: 'pointer',
    borderBottom: '1px solid #eeeeee',
    '&:hover': { background: '#f5f5f5' },
  },
  groupRowOpen: { background: '#f5f7ff', '&:hover': { background: '#eef0fb' } },
  chevron: { color: '#9e9e9e', transition: 'transform 0.15s' },
  chevronOpen: { transform: 'rotate(90deg)' },
  groupIcon: { margin: '0 8px 0 2px' },
  groupName: { flex: 1, minWidth: 0, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  meta: { flexShrink: 0, marginLeft: 8, color: '#757575', fontSize: 12 },
  fileRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '3px 4px 3px 40px',
    borderBottom: '1px solid #f5f5f5',
    '&:hover': { background: '#fafafa' },
  },
  fileIcon: { fontSize: 16, color: '#9e9e9e', marginRight: 8 },
  fileName: { flex: 1, minWidth: 0, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  fileDir: { color: '#9e9e9e' },
  showMore: { padding: '4px 0 6px 40px' },
});

export const formatBytes = (bytes) => {
  if (!bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${units[i]}`;
};

const isIgnored = (name) => IGNORED_FILES.includes(name);

// readEntries returns at most ~100 entries per call, so keep reading until it returns empty
const readAllEntries = (reader) => new Promise((resolve, reject) => {
  const entries = [];
  const readBatch = () => reader.readEntries((batch) => {
    if (!batch.length) {
      resolve(entries);
    } else {
      entries.push(...batch);
      readBatch();
    }
  }, reject);
  readBatch();
});

const collectEntryFiles = async (entry, out) => {
  if (entry.isFile) {
    const file = await new Promise((resolve, reject) => entry.file(resolve, reject));
    if (!isIgnored(file.name)) {
      out.push({ file, relativePath: entry.fullPath.replace(/^\//, '') });
    }
  } else if (entry.isDirectory) {
    const children = await readAllEntries(entry.createReader());
    for (const child of children) {
      await collectEntryFiles(child, out);
    }
  }
};

const fromFileList = (fileList) => Array.from(fileList)
  .filter(file => !isIgnored(file.name))
  .map(file => ({ file, relativePath: file.webkitRelativePath || file.name }));

const StreamUploadModal = ({ dataRoom, open, onClose, onStartUpload, starting = false }) => {
  const classes = useStyles();
  const [items, setItems] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [visibleCounts, setVisibleCounts] = useState({});
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  // React 17 doesn't forward webkitdirectory, so set it on the DOM node. A callback ref is needed because
  // the Dialog mounts its content in a portal after the first render, so a mount effect would see a null ref.
  const setFolderInputRef = useCallback((node) => {
    folderInputRef.current = node;
    if (node) {
      node.setAttribute('webkitdirectory', '');
      node.setAttribute('directory', '');
    }
  }, []);

  const addItems = (newItems) => {
    setItems(prevItems => {
      // Same relative path selected twice replaces the earlier one
      const byPath = new Map(prevItems.map(item => [item.relativePath, item]));
      newItems.forEach(item => byPath.set(item.relativePath, item));
      return Array.from(byPath.values());
    });
  };

  const onInputChange = (evt) => {
    addItems(fromFileList(evt.target.files));
    evt.target.value = ''; // allow re-selecting the same folder
  };

  const onDrop = async (evt) => {
    evt.preventDefault();
    setDragActive(false);
    // Entries must be read synchronously before the first await, the DataTransfer is cleared afterwards
    const entries = Array.from(evt.dataTransfer.items || [])
      .filter(item => item.kind === 'file')
      .map(item => item.webkitGetAsEntry ? item.webkitGetAsEntry() : null);
    if (!entries.length || entries.some(entry => !entry)) {
      addItems(fromFileList(evt.dataTransfer.files));
      return;
    }
    setScanning(true);
    try {
      const collected = [];
      for (const entry of entries) {
        await collectEntryFiles(entry, collected);
      }
      addItems(collected);
    } finally {
      setScanning(false);
    }
  };

  // Group by top-level folder/file so 10k files render as a handful of rows
  const groups = useMemo(() => {
    const map = new Map();
    items.forEach(({ file, relativePath }) => {
      const parts = relativePath.split('/');
      const isFolder = parts.length > 1;
      const name = parts[0];
      const key = `${isFolder ? 'd' : 'f'}:${name}`;
      const group = map.get(key) || { key, name, isFolder, count: 0, size: 0, files: [] };
      group.count += 1;
      group.size += file.size;
      group.files.push({ file, relativePath, subPath: parts.slice(1).join('/') });
      map.set(key, group);
    });
    const sorted = Array.from(map.values()).sort((a, b) => (b.isFolder - a.isFolder) || a.name.localeCompare(b.name));
    sorted.forEach(group => group.files.sort((a, b) => a.relativePath.localeCompare(b.relativePath)));
    return sorted;
  }, [items]);

  const folderCount = groups.filter(group => group.isFolder).length;

  const totalSize = useMemo(() => items.reduce((sum, item) => sum + item.file.size, 0), [items]);

  const removeGroup = (group) => {
    setItems(prevItems => prevItems.filter(({ relativePath }) => {
      const parts = relativePath.split('/');
      return !(parts[0] === group.name && (parts.length > 1) === group.isFolder);
    }));
  };

  const removeFile = (relativePath) => {
    setItems(prevItems => prevItems.filter(item => item.relativePath !== relativePath));
  };

  const clearAll = () => {
    setItems([]);
    setExpanded({});
    setVisibleCounts({});
  };

  const handleClose = () => {
    clearAll();
    onClose();
  };

  const folderName = dataRoom.navigation && dataRoom.navigation.length ? dataRoom.navigation[dataRoom.navigation.length - 1].name : 'loading...';

  return (
    <Dialog open={open} onClose={scanning || starting ? undefined : handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Stream Upload To "<b>{folderName}</b>"
      </DialogTitle>
      <DialogContent dividers>
        <div
          className={`${classes.dropArea} ${items.length ? classes.dropAreaCompact : ''} ${dragActive ? classes.dropAreaActive : ''}`}
          onDragOver={(evt) => { evt.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={onDrop}
        >
          {items.length ? (
            <Typography variant="body2" color="textSecondary">
              <CloudUploadIcon className={classes.uploadIconCompact} />Drop more files or folders here
            </Typography>
          ) : (
            <>
              <CloudUploadIcon className={classes.uploadIcon} />
              <Typography variant="body1">Drag & drop files or folders here</Typography>
              <Typography variant="caption" color="textSecondary">or</Typography>
            </>
          )}
          <div className={items.length ? classes.pickButtonsCompact : classes.pickButtons}>
            <Button disabled={scanning} onClick={() => fileInputRef.current.click()} variant="outlined" color="primary" size="small" startIcon={<NoteAddIcon />}>Select Files</Button>
            <Button disabled={scanning} onClick={() => folderInputRef.current.click()} variant="outlined" color="primary" size="small" startIcon={<CreateNewFolderIcon />}>Select Folder</Button>
          </div>
          <input ref={fileInputRef} type="file" multiple hidden onChange={onInputChange} />
          <input ref={setFolderInputRef} type="file" multiple hidden onChange={onInputChange} />
        </div>
        {scanning ? (
          <div style={{ marginTop: 16 }}>
            <Typography variant="caption" color="textSecondary">Reading folder contents...</Typography>
            <LinearProgress />
          </div>
        ) : null}
        {items.length ? (
          <>
            <div className={classes.summary}>
              <Typography variant="subtitle2">
                {folderCount ? `${folderCount.toLocaleString()} folder${folderCount > 1 ? 's' : ''} · ` : ''}
                {items.length.toLocaleString()} file{items.length > 1 ? 's' : ''} selected ({formatBytes(totalSize)})
              </Typography>
              <Button size="small" onClick={clearAll}>Clear All</Button>
            </div>
            <div className={classes.list}>
              {groups.map(group => {
                if (!group.isFolder) {
                  const { relativePath, file } = group.files[0];
                  return (
                    <div key={group.key} className={classes.groupRow} style={{ cursor: 'default', paddingLeft: 30 }}>
                      <InsertDriveFileIcon color="primary" fontSize="small" className={classes.groupIcon} />
                      <Typography variant="body2" className={classes.groupName} title={relativePath}>{group.name}</Typography>
                      <span className={classes.meta}>{formatBytes(file.size)}</span>
                      <Tooltip title="Remove" arrow>
                        <IconButton size="small" onClick={() => removeFile(relativePath)}><CloseIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </div>
                  );
                }
                // A lone folder opens by default, there is nothing else to scan past
                const isOpen = expanded[group.key] !== undefined ? expanded[group.key] : groups.length === 1;
                const visible = visibleCounts[group.key] || FOLDER_PAGE_SIZE;
                const remaining = group.files.length - visible;
                return (
                  <div key={group.key}>
                    <div
                      role="button"
                      tabIndex={0}
                      aria-expanded={isOpen}
                      className={`${classes.groupRow} ${isOpen ? classes.groupRowOpen : ''}`}
                      onClick={() => setExpanded(prev => ({ ...prev, [group.key]: !isOpen }))}
                      onKeyDown={(evt) => { if (evt.key === 'Enter' || evt.key === ' ') { evt.preventDefault(); setExpanded(prev => ({ ...prev, [group.key]: !isOpen })); } }}
                    >
                      <ChevronRightIcon className={`${classes.chevron} ${isOpen ? classes.chevronOpen : ''}`} />
                      {isOpen ? <FolderOpenIcon color="primary" fontSize="small" className={classes.groupIcon} /> : <FolderIcon color="primary" fontSize="small" className={classes.groupIcon} />}
                      <Typography variant="body2" className={classes.groupName} title={group.name}>{group.name}</Typography>
                      <span className={classes.meta}>{group.count.toLocaleString()} file{group.count > 1 ? 's' : ''} · {formatBytes(group.size)}</span>
                      <Tooltip title="Remove folder" arrow>
                        <IconButton size="small" onClick={(evt) => { evt.stopPropagation(); removeGroup(group); }}><DeleteOutlineIcon fontSize="small" color="error" /></IconButton>
                      </Tooltip>
                    </div>
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                      {group.files.slice(0, visible).map(({ file, relativePath, subPath }) => {
                        const slash = subPath.lastIndexOf('/');
                        return (
                          <div key={relativePath} className={classes.fileRow}>
                            <InsertDriveFileIcon className={classes.fileIcon} />
                            <span className={classes.fileName} title={relativePath}>
                              {slash > -1 ? <span className={classes.fileDir}>{subPath.slice(0, slash + 1)}</span> : null}
                              {file.name}
                            </span>
                            <span className={classes.meta}>{formatBytes(file.size)}</span>
                            <Tooltip title="Remove" arrow>
                              <IconButton size="small" onClick={() => removeFile(relativePath)}><CloseIcon style={{ fontSize: 16 }} /></IconButton>
                            </Tooltip>
                          </div>
                        );
                      })}
                      {remaining > 0 ? (
                        <div className={classes.showMore}>
                          <Button size="small" color="primary" onClick={() => setVisibleCounts(prev => ({ ...prev, [group.key]: visible + FOLDER_PAGE_SIZE }))}>
                            Show {Math.min(FOLDER_PAGE_SIZE, remaining).toLocaleString()} more ({remaining.toLocaleString()} remaining)
                          </Button>
                        </div>
                      ) : null}
                    </Collapse>
                  </div>
                );
              })}
            </div>
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button color="primary" disabled={scanning || starting} onClick={handleClose}>Cancel</Button>
        <Button
          color="primary"
          variant="contained"
          disabled={scanning || starting || !items.length}
          onClick={() => onStartUpload(items)}
        >
          {starting ? 'Starting...' : 'Start Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StreamUploadModal;
